/**
 * ComplianceCenter.jsx
 *
 * Enterprise compliance dashboard for GDPR / NIS2 / ISO 27001 auditing.
 * Restricted to super_admin and the designated DPO (asif@intellixit.com).
 *
 * Sections:
 *   1. Summary cards — total events, login failures, data exports, chain status
 *   2. Audit log viewer — paginated, filterable table
 *   3. Report generator — date range picker → generate/download PDF
 *   4. Chain integrity verifier — one-click hash chain check
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
  ShieldCheck,
  FileText,
  Download,
  Loader2,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Search,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Lock,
  Users,
  Activity,
  FileDown,
  Link2,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import {
  getComplianceLogs,
  getComplianceSummary,
  getComplianceActions,
  generateComplianceReport,
  listComplianceReports,
  downloadComplianceReport,
  verifyComplianceChain,
} from '../lib/api';

/* ---------- helper: severity badge colour ---------- */
function SeverityBadge({ severity }) {
  const map = {
    info: 'bg-sky-100 text-sky-700 border-sky-200',
    warning: 'bg-amber-100 text-amber-700 border-amber-200',
    critical: 'bg-red-100 text-red-700 border-red-200',
  };
  return (
    <span
      data-testid={`severity-badge-${severity}`}
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${map[severity] || map.info}`}
    >
      {severity}
    </span>
  );
}

/* ---------- helper: category badge ---------- */
function CategoryBadge({ category }) {
  const map = {
    auth: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    data: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    admin: 'bg-violet-100 text-violet-700 border-violet-200',
    security: 'bg-rose-100 text-rose-700 border-rose-200',
    stock_return: 'bg-amber-100 text-amber-700 border-amber-200',
    system: 'bg-slate-100 text-slate-700 border-slate-200',
    general: 'bg-zinc-100 text-zinc-700 border-zinc-200',
  };
  const label = category === 'stock_return' ? 'return' : category;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${map[category] || map.general}`}>
      {label}
    </span>
  );
}

/* ---------- helper: return-set (matches backend audit_service) ---------- */
const RETURN_ACTIONS = [
  'stock_return',
  'returns_create',
  'void_stock_return',
  'reverse_dispatch',
  'undo_reverse_dispatch',
];

/* ---------- helper: derive readable description from log payload ----------
 *
 * Backend now writes rich descriptions (post Feb 2026 fix), but 4,000+
 * legacy entries carry the old opaque default ("stock_return on
 * dispatch/<uuid>"). This helper reconstructs the readable form from
 * before_value / after_value so historic entries look native too.
 */
function readableDescription(log) {
  const d = log?.description || '';
  // If the backend already wrote a rich description (post-fix), use it.
  if (d && !/^[a-z_]+ on [a-z_]+\/[0-9a-f-]{8,}/i.test(d)) return d;

  const after = log?.after_value || {};
  const before = log?.before_value || {};
  const a = log?.action || '';

  if (a === 'stock_return' || a === 'returns_create') {
    const rn = after.return_number || '—';
    const dn = after.dispatch_number || '';
    const qty = after.total_quantity_returned;
    const reason = (after.reason || '').trim();
    const parts = [`Return ${rn} recorded`];
    const tail = [];
    if (dn) tail.push(`dispatch ${dn}`);
    if (qty != null) tail.push(`${qty} units`);
    let base = parts[0];
    if (tail.length) base += ' — ' + tail.join(', ');
    if (reason) base += `. Reason: "${reason.slice(0, 120)}"`;
    return base;
  }
  if (a === 'void_stock_return') {
    const rn = before.return_number || after.return_number || '—';
    const reason = (after.reason || '').trim();
    let base = `Return ${rn} VOIDED`;
    if (reason) base += `. Reason: "${reason.slice(0, 120)}"`;
    return base;
  }
  if (a === 'reverse_dispatch') {
    const dn = after.dispatch_number || before.dispatch_number || '';
    const reason = (after.reason || '').trim();
    let base = dn ? `Dispatch ${dn} REVERSED` : 'Dispatch reversed';
    if (reason) base += `. Reason: "${reason.slice(0, 120)}"`;
    return base;
  }
  if (a === 'undo_reverse_dispatch') {
    const dn = after.dispatch_number || before.dispatch_number || '';
    return dn ? `Dispatch ${dn} reversal UNDONE` : 'Dispatch reversal undone';
  }
  // Dispatch verbs
  if (log?.entity_type === 'dispatch') {
    const dn = after.dispatch_number || before.dispatch_number || '';
    const cust = after.customer_name || before.customer_name || '';
    const verbMap = {
      dispatches_create: 'created',
      dispatches_update: 'updated',
      dispatches_delete: 'deleted',
      create: 'created',
      delete: 'deleted',
      download_pdf: 'PDF downloaded',
      email_sent: 'emailed',
      clear_dispatch_invoice: 'invoice number cleared',
      invoice_flag_toggled: 'invoice flag toggled',
    };
    const verb = verbMap[a];
    if (dn && verb) return `Dispatch ${dn} ${verb}${cust ? ` (${cust})` : ''}`;
  }
  // Fallback to whatever the backend stored
  return d || `${a.replace(/_/g, ' ')}`;
}

/* ---------- helper: extract linkable entity handle from a log ---------- */
function entityHandle(log) {
  const after = log?.after_value || {};
  const before = log?.before_value || {};
  return (
    after.return_number ||
    before.return_number ||
    after.dispatch_number ||
    before.dispatch_number ||
    after.recall_number ||
    before.recall_number ||
    log?.entity_id ||
    ''
  );
}

export default function ComplianceCenter() {
  const { user } = useAuth();
  const navigate = useNavigate();

  /* ---- state ---- */
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsMeta, setLogsMeta] = useState({ total: 0, page: 1, total_pages: 1 });
  const [actions, setActions] = useState([]);
  const [reports, setReports] = useState([]);
  const [chainResult, setChainResult] = useState(null);

  /* filters */
  const [filters, setFilters] = useState({
    page: 1, page_size: 25, category: '', action: '', action_in: '', severity: '', search: '',
    date_from: '', date_to: '', entity_id: '', user_email: '',
  });

  /* expanded rows (per-log-id boolean map) */
  const [expandedRows, setExpandedRows] = useState({});

  /* report generation */
  const [reportDateFrom, setReportDateFrom] = useState('');
  const [reportDateTo, setReportDateTo] = useState('');
  const [generating, setGenerating] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  /* ---- data loading ---- */
  const loadSummary = useCallback(async () => {
    try {
      const { data } = await getComplianceSummary();
      setSummary(data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Access restricted to DPO or super admin');
        navigate('/');
      }
    }
  }, [navigate]);

  const loadLogs = useCallback(async () => {
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await getComplianceLogs(params);
      setLogs(data.logs || []);
      setLogsMeta({ total: data.total, page: data.page, total_pages: data.total_pages });
    } catch {
      toast.error('Failed to load compliance logs');
    }
  }, [filters]);

  const loadActions = useCallback(async () => {
    try {
      const { data } = await getComplianceActions();
      setActions(data.actions || []);
    } catch { /* ignore */ }
  }, []);

  const loadReports = useCallback(async () => {
    try {
      const { data } = await listComplianceReports({ page: 1, page_size: 20 });
      setReports(data.reports || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadSummary(), loadLogs(), loadActions(), loadReports()]);
      setLoading(false);
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadLogs(); }, [loadLogs]);

  /* ---- handlers ---- */
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: key === 'page' ? value : 1 }));
  };

  const handleGenerateReport = async () => {
    if (!reportDateFrom || !reportDateTo) {
      toast.error('Select a date range');
      return;
    }
    setGenerating(true);
    try {
      await generateComplianceReport({ date_from: reportDateFrom, date_to: reportDateTo });
      toast.success('Report generated successfully');
      loadReports();
      loadSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadReport = async (id) => {
    setDownloadingId(id);
    try {
      const { data } = await downloadComplianceReport(id);
      const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `compliance_report_${id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download report');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleVerifyChain = async () => {
    setVerifying(true);
    try {
      const { data } = await verifyComplianceChain();
      setChainResult(data);
      if (data.is_valid) {
        toast.success(`Chain verified: ${data.verified_entries} entries intact`);
      } else {
        toast.error(`Chain broken at sequence #${data.first_broken_at}`);
      }
    } catch {
      toast.error('Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="compliance-center" className="space-y-6 pb-10">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/settings')} data-testid="compliance-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-emerald-600" /> Compliance Center
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              GDPR &middot; NIS2 &middot; ISO/IEC 27001:2022 — Audit &amp; Reporting
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={handleVerifyChain} disabled={verifying} data-testid="verify-chain-btn">
          {verifying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link2 className="w-4 h-4 mr-2" />}
          Verify Chain Integrity
        </Button>
      </div>

      {/* Chain result banner */}
      {chainResult && (
        <div
          data-testid="chain-result-banner"
          className={`flex items-center gap-3 p-3 rounded-lg border text-sm ${
            chainResult.is_valid
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          {chainResult.is_valid ? (
            <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
          ) : (
            <XCircle className="w-5 h-5 flex-shrink-0" />
          )}
          <span>
            {chainResult.is_valid
              ? `Hash chain intact — ${chainResult.verified_entries} entries verified`
              : `Chain broken at sequence #${chainResult.first_broken_at} (${chainResult.verified_entries}/${chainResult.total_entries} verified)`}
          </span>
        </div>
      )}

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card data-testid="summary-total-events">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-slate-100"><Activity className="w-5 h-5 text-slate-600" /></div>
                <div>
                  <p className="text-xs text-muted-foreground">Total Events</p>
                  <p className="text-2xl font-bold">{summary.total_entries}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="summary-login-failures">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-100"><AlertTriangle className="w-5 h-5 text-amber-600" /></div>
                <div>
                  <p className="text-xs text-muted-foreground">Login Failures</p>
                  <p className="text-2xl font-bold">{summary.login_failures}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="summary-data-exports">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-sky-100"><FileDown className="w-5 h-5 text-sky-600" /></div>
                <div>
                  <p className="text-xs text-muted-foreground">Data Exports</p>
                  <p className="text-2xl font-bold">{summary.data_exports}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="summary-reports-generated">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-violet-100"><FileText className="w-5 h-5 text-violet-600" /></div>
                <div>
                  <p className="text-xs text-muted-foreground">Reports Generated</p>
                  <p className="text-2xl font-bold">{summary.report_count}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Severity & Category breakdown */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold">By Severity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {Object.entries(summary.severity_counts || {}).map(([sev, count]) => (
                  <div key={sev} className="flex items-center gap-2">
                    <SeverityBadge severity={sev} />
                    <span className="text-sm font-medium">{count}</span>
                  </div>
                ))}
                {Object.keys(summary.severity_counts || {}).length === 0 && (
                  <span className="text-sm text-muted-foreground">No data yet</span>
                )}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold">By Category</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {Object.entries(summary.category_counts || {}).map(([cat, count]) => (
                  <div key={cat} className="flex items-center gap-2">
                    <CategoryBadge category={cat} />
                    <span className="text-sm font-medium">{count}</span>
                  </div>
                ))}
                {Object.keys(summary.category_counts || {}).length === 0 && (
                  <span className="text-sm text-muted-foreground">No data yet</span>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Report Generator */}
      <Card data-testid="report-generator">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <FileText className="w-4 h-4" /> Generate Audit Report
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">From</label>
              <Input
                data-testid="report-date-from"
                type="date"
                value={reportDateFrom}
                onChange={(e) => setReportDateFrom(e.target.value)}
                className="w-40"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">To</label>
              <Input
                data-testid="report-date-to"
                type="date"
                value={reportDateTo}
                onChange={(e) => setReportDateTo(e.target.value)}
                className="w-40"
              />
            </div>
            <Button onClick={handleGenerateReport} disabled={generating} data-testid="generate-report-btn">
              {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
              Generate PDF Report
            </Button>
          </div>

          {/* Previous reports */}
          {reports.length > 0 && (
            <div className="mt-4">
              <h4 className="text-xs font-semibold text-muted-foreground mb-2">Previous Reports</h4>
              <div className="space-y-2">
                {reports.map((r) => (
                  <div key={r.id} className="flex items-center justify-between p-2 rounded border bg-muted/30 text-sm" data-testid={`report-row-${r.id}`}>
                    <div className="flex items-center gap-3">
                      <FileText className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <span className="font-medium">{r.title}</span>
                        <span className="text-xs text-muted-foreground ml-2">
                          {r.total_entries} entries &middot; {(r.file_size_bytes / 1024).toFixed(1)} KB
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">{r.generated_at?.slice(0, 10)}</span>
                      <Button
                        variant="ghost" size="icon"
                        onClick={() => handleDownloadReport(r.id)}
                        disabled={downloadingId === r.id}
                        data-testid={`download-report-${r.id}`}
                      >
                        {downloadingId === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Audit Log Viewer */}
      <Card data-testid="audit-log-viewer">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Lock className="w-4 h-4" /> Audit Log ({logsMeta.total} entries)
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={() => { loadLogs(); loadSummary(); }} data-testid="refresh-logs-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-wrap items-end gap-2 mb-3">
            <div className="flex-1 min-w-[180px]">
              <Input
                data-testid="log-search-input"
                placeholder="Search logs (description, action, email)..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="h-9"
              />
            </div>
            <Select value={filters.category || 'all'} onValueChange={(v) => handleFilterChange('category', v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[130px] h-9" data-testid="filter-category">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="auth">Auth</SelectItem>
                <SelectItem value="data">Data</SelectItem>
                <SelectItem value="stock_return">Stock Return</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="security">Security</SelectItem>
                <SelectItem value="system">System</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.severity || 'all'} onValueChange={(v) => handleFilterChange('severity', v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[120px] h-9" data-testid="filter-severity">
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.action || 'all'} onValueChange={(v) => handleFilterChange('action', v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[160px] h-9" data-testid="filter-action">
                <SelectValue placeholder="Action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {actions.map((a) => (
                  <SelectItem key={a} value={a}>{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="date"
              className="w-[140px] h-9"
              value={filters.date_from}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
              data-testid="filter-date-from"
            />
            <Input
              type="date"
              className="w-[140px] h-9"
              value={filters.date_to}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
              data-testid="filter-date-to"
            />
          </div>

          {/* Quick filter chips + active-filter pills */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Quick filters:</span>
            <button
              type="button"
              onClick={() => handleFilterChange('action_in', RETURN_ACTIONS.join(','))}
              className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                filters.action_in === RETURN_ACTIONS.join(',')
                  ? 'bg-amber-100 border-amber-400 text-amber-800 font-semibold'
                  : 'border-amber-200 text-amber-700 hover:bg-amber-50'
              }`}
              data-testid="quick-filter-returns"
            >
              Returns &amp; Reversals
            </button>
            <button
              type="button"
              onClick={() => handleFilterChange('action_in', 'auth_login_failed,account_locked')}
              className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                filters.action_in === 'auth_login_failed,account_locked'
                  ? 'bg-rose-100 border-rose-400 text-rose-800 font-semibold'
                  : 'border-rose-200 text-rose-700 hover:bg-rose-50'
              }`}
              data-testid="quick-filter-security"
            >
              Failed Logins
            </button>
            <button
              type="button"
              onClick={() => handleFilterChange('action_in', 'download_pdf,export,data_export_get,download_recall_pdf,download_recall_per_customer_zip')}
              className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                filters.action_in?.startsWith('download_pdf,export')
                  ? 'bg-sky-100 border-sky-400 text-sky-800 font-semibold'
                  : 'border-sky-200 text-sky-700 hover:bg-sky-50'
              }`}
              data-testid="quick-filter-exports"
            >
              Data Exports
            </button>

            {/* Active drill-down pills (removable) */}
            {filters.entity_id && (
              <span className="ml-2 inline-flex items-center gap-1 bg-amber-50 border border-amber-300 text-amber-800 text-xs px-2 py-0.5 rounded" data-testid="active-filter-entity">
                Entity: <span className="font-mono">{filters.entity_id}</span>
                <button
                  type="button"
                  onClick={() => handleFilterChange('entity_id', '')}
                  className="ml-1 hover:text-amber-900"
                  aria-label="Clear entity filter"
                >
                  <XCircle className="w-3 h-3" />
                </button>
              </span>
            )}
            {filters.user_email && (
              <span className="inline-flex items-center gap-1 bg-indigo-50 border border-indigo-300 text-indigo-800 text-xs px-2 py-0.5 rounded" data-testid="active-filter-user">
                User: <span className="font-mono">{filters.user_email}</span>
                <button
                  type="button"
                  onClick={() => handleFilterChange('user_email', '')}
                  className="ml-1 hover:text-indigo-900"
                  aria-label="Clear user filter"
                >
                  <XCircle className="w-3 h-3" />
                </button>
              </span>
            )}
            {filters.action_in && (
              <span className="inline-flex items-center gap-1 bg-slate-100 border border-slate-300 text-slate-700 text-xs px-2 py-0.5 rounded" data-testid="active-filter-action-in">
                {filters.action_in.split(',').length} action{filters.action_in.split(',').length > 1 ? 's' : ''}
                <button
                  type="button"
                  onClick={() => handleFilterChange('action_in', '')}
                  className="ml-1 hover:text-slate-900"
                  aria-label="Clear action filter"
                >
                  <XCircle className="w-3 h-3" />
                </button>
              </span>
            )}
          </div>

          {/* Table */}
          <div className="rounded-md border overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[36px]"></TableHead>
                  <TableHead className="w-[40px]">#</TableHead>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead className="min-w-[260px]">Description</TableHead>
                  <TableHead>Entity</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                      No log entries found
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => {
                    const isOpen = !!expandedRows[log.id];
                    const desc = readableDescription(log);
                    const handle = entityHandle(log);
                    return (
                      <React.Fragment key={log.id}>
                        <TableRow
                          data-testid={`log-row-${log.sequence}`}
                          className="cursor-pointer hover:bg-muted/40"
                          onClick={() => setExpandedRows((p) => ({ ...p, [log.id]: !p[log.id] }))}
                        >
                          <TableCell className="text-muted-foreground">
                            {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">{log.sequence}</TableCell>
                          <TableCell className="text-xs whitespace-nowrap">
                            {log.timestamp?.replace('T', ' ').slice(0, 19)}
                          </TableCell>
                          <TableCell
                            className="text-xs max-w-[170px] truncate"
                            title={`Click to filter by ${log.user_email}`}
                          >
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleFilterChange('user_email', log.user_email); }}
                              className="text-left hover:text-primary hover:underline truncate max-w-full"
                              data-testid={`log-user-btn-${log.sequence}`}
                            >
                              {log.user_email}
                            </button>
                          </TableCell>
                          <TableCell className="text-xs font-mono">{log.action}</TableCell>
                          <TableCell><CategoryBadge category={log.category} /></TableCell>
                          <TableCell><SeverityBadge severity={log.severity} /></TableCell>
                          <TableCell className="text-xs max-w-[380px]" title={desc}>
                            <span className={isOpen ? '' : 'line-clamp-2 block'}>{desc}</span>
                          </TableCell>
                          <TableCell className="text-xs">
                            {handle ? (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); handleFilterChange('entity_id', handle); }}
                                className="font-mono text-primary hover:underline"
                                title={`Show full timeline for ${handle}`}
                                data-testid={`log-entity-btn-${log.sequence}`}
                              >
                                {handle.length > 14 ? handle.slice(0, 8) + '…' : handle}
                              </button>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                        </TableRow>

                        {isOpen && (
                          <TableRow className="bg-muted/20 hover:bg-muted/20">
                            <TableCell colSpan={9} className="p-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                                <div>
                                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Metadata</div>
                                  <div className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1">
                                    <span className="text-muted-foreground">User role</span><span className="font-mono">{log.user_role || '—'}</span>
                                    <span className="text-muted-foreground">Entity type</span><span className="font-mono">{log.entity_type || '—'}</span>
                                    <span className="text-muted-foreground">Entity ID</span><span className="font-mono break-all">{log.entity_id || '—'}</span>
                                    <span className="text-muted-foreground">IP address</span><span className="font-mono">{log.ip_address || '—'}</span>
                                    <span className="text-muted-foreground">User agent</span><span className="font-mono break-all">{log.user_agent || '—'}</span>
                                    <span className="text-muted-foreground">Chain seq.</span><span className="font-mono">{log.sequence}</span>
                                  </div>
                                </div>
                                <div className="space-y-2">
                                  {log.before_value && Object.keys(log.before_value).length > 0 && (
                                    <div>
                                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Before</div>
                                      <pre className="bg-background border rounded p-2 overflow-x-auto text-[11px]">{JSON.stringify(log.before_value, null, 2)}</pre>
                                    </div>
                                  )}
                                  {log.after_value && Object.keys(log.after_value).length > 0 && (
                                    <div>
                                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">After</div>
                                      <pre className="bg-background border rounded p-2 overflow-x-auto text-[11px]">{JSON.stringify(log.after_value, null, 2)}</pre>
                                    </div>
                                  )}
                                  {log.metadata && Object.keys(log.metadata).length > 0 && (
                                    <div>
                                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Metadata</div>
                                      <pre className="bg-background border rounded p-2 overflow-x-auto text-[11px]">{JSON.stringify(log.metadata, null, 2)}</pre>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {logsMeta.total_pages > 1 && (
            <div className="flex items-center justify-between mt-3">
              <span className="text-xs text-muted-foreground">
                Page {logsMeta.page} of {logsMeta.total_pages} ({logsMeta.total} entries)
              </span>
              <div className="flex gap-1">
                <Button
                  variant="outline" size="icon" className="h-8 w-8"
                  disabled={logsMeta.page <= 1}
                  onClick={() => handleFilterChange('page', logsMeta.page - 1)}
                  data-testid="logs-prev-page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline" size="icon" className="h-8 w-8"
                  disabled={logsMeta.page >= logsMeta.total_pages}
                  onClick={() => handleFilterChange('page', logsMeta.page + 1)}
                  data-testid="logs-next-page"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
